import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { toast } from '../utils/toast';
import { TrendingUp, TrendingDown, Star, Edit2, Save, X, Camera, Trash2 } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const StaffProfileModal = ({ isOpen, onClose, onProfileUpdated }) => {
  const [loading, setLoading] = useState(true);
  const [profileData, setProfileData] = useState(null);
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', password: '' });
  const [completedTopics, setCompletedTopics] = useState([]);
  const [loadingTopics, setLoadingTopics] = useState(true);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [photoRemoving, setPhotoRemoving] = useState(false);
  const [isMobileProfileSheet, setIsMobileProfileSheet] = useState(() => (
    typeof window !== 'undefined' ? window.matchMedia('(max-width: 767px)').matches : false
  ));

  useEffect(() => {
    if (isOpen) {
      fetchProfile();
      fetchCompletedTopics();
    }
  }, [isOpen]);

  useEffect(() => {
    const updateMobileState = () => setIsMobileProfileSheet(window.matchMedia('(max-width: 767px)').matches);
    updateMobileState();
    window.addEventListener('resize', updateMobileState);
    window.addEventListener('orientationchange', updateMobileState);
    return () => {
      window.removeEventListener('resize', updateMobileState);
      window.removeEventListener('orientationchange', updateMobileState);
    };
  }, []);

  const fetchProfile = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/staff/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProfileData(response.data);
      setFormData({
        name: response.data.profile.name,
        email: response.data.profile.email,
        phone: response.data.profile.phone,
        password: ''
      });
    } catch (error) {
      console.error('Failed to fetch profile:', error);
      toast.error('Failed to fetch profile');
    } finally {
      setLoading(false);
    }
  };

  const fetchCompletedTopics = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/staff/profile/completed-topics`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCompletedTopics(response.data.completed_topics || []);
    } catch (error) {
      console.error('Failed to fetch completed topics:', error);
      toast.error('Failed to fetch completed topics');
    } finally {
      setLoadingTopics(false);
    }
  };

  const handleSave = async () => {
    try {
      const token = localStorage.getItem('token');
      const updateData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone
      };
      
      // Only include password if it's been changed
      if (formData.password && formData.password.trim() !== '') {
        updateData.password = formData.password;
      }

      await axios.put(`${API}/api/staff/profile/update`, updateData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Refresh profile data first so updated values are visible
      await fetchProfile();
      
      // Exit edit mode and clear password field
      setEditing(false);
      setFormData(prev => ({ ...prev, password: '' }));
      
      // Show success notification after data is refreshed
      toast.success('Profile updated successfully');
    } catch (error) {
      console.error('Failed to update profile:', error);
      toast.error('Failed to update profile');
    }
  };

  const syncUpdatedUser = (updatedUser) => {
    if (!updatedUser) return;
    const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
    const mergedUser = { ...storedUser, ...updatedUser };
    localStorage.setItem('user', JSON.stringify(mergedUser));
    onProfileUpdated?.(mergedUser);
  };

  const handlePhotoUpload = async (event) => {
    const selectedFile = event.target.files?.[0];
    event.target.value = '';
    if (!selectedFile) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(selectedFile.type)) {
      toast.error('Please upload a JPG, JPEG, PNG, or WEBP image.');
      return;
    }
    if (selectedFile.size > 5 * 1024 * 1024) {
      toast.error('Profile photo must be 5 MB or smaller.');
      return;
    }

    try {
      setPhotoUploading(true);
      const token = localStorage.getItem('token');
      const payload = new FormData();
      payload.append('file', selectedFile);
      const response = await axios.post(`${API}/api/staff/profile/photo`, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      setProfileData(prev => ({
        ...prev,
        profile: {
          ...prev.profile,
          profile_photo_url: response.data.profile_photo_url,
          profile_photo_s3_key: response.data.profile_photo_s3_key
        }
      }));
      syncUpdatedUser(response.data.user);
      toast.success('Profile photo updated successfully');
    } catch (error) {
      console.error('Failed to upload profile photo:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload profile photo');
    } finally {
      setPhotoUploading(false);
    }
  };

  const handlePhotoRemove = async () => {
    try {
      setPhotoRemoving(true);
      const token = localStorage.getItem('token');
      const response = await axios.delete(`${API}/api/staff/profile/photo`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProfileData(prev => ({
        ...prev,
        profile: {
          ...prev.profile,
          profile_photo_url: null,
          profile_photo_s3_key: null
        }
      }));
      syncUpdatedUser(response.data.user);
      toast.success('Profile photo removed');
    } catch (error) {
      console.error('Failed to remove profile photo:', error);
      toast.error(error.response?.data?.detail || 'Failed to remove profile photo');
    } finally {
      setPhotoRemoving(false);
    }
  };

  if (loading) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="staff-profile-modal-content staff-mobile-sheet max-w-6xl max-h-[90vh] overflow-hidden" hideClose>
        <DialogTitle className="sr-only">Loading Profile</DialogTitle>
        <div className="flex justify-center items-center py-12">
            <div className="w-12 h-12 border-4 border-[#0056B3] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </DialogContent>
    </Dialog>
    );
  }

  if (!profileData) return null;

  const profilePhotoUrl = profileData.profile?.profile_photo_url || null;
  const profileInitial = profileData.profile?.name?.charAt(0)?.toUpperCase() || 'U';

  // The mobile sheet is rendered directly in document.body, outside Radix Dialog.
  // Use a native heading there because DialogTitle requires a Dialog provider.
  const ProfileHeading = isMobileProfileSheet ? 'h2' : DialogTitle;

  const profileHeader = (
        <div className="bg-white flex-shrink-0">
          <div className="staff-profile-sheet-handle hidden justify-center pt-2 pb-1">
            <div className="h-1.5 w-12 rounded-full bg-gray-300" />
          </div>
          <div className="staff-profile-sheet-header flex items-center justify-between px-4 pb-3 border-b border-gray-100">
          <ProfileHeading className="staff-profile-title text-2xl font-bold" style={{ color: '#0056B3' }}>My Profile</ProfileHeading>
          <button
            onClick={() => onClose(false)}
            className="popup-close-button"
            aria-label="Close"
            type="button"
          >
            <X className="w-3.5 h-3.5 text-white" />
          </button>
          </div>
        </div>
  );

  const profileBody = (
        <div className="staff-profile-body hide-scrollbar flex-1 overflow-y-auto overflow-x-hidden p-6 space-y-6">
          {/* Personal Information */}
          <Card className="staff-profile-card p-6">
            <div className="staff-profile-section-header flex justify-between items-center mb-4">
              <h2 className="staff-profile-section-title text-[18px] font-bold" style={{ color: '#0056B3' }}>Personal Information</h2>
              {!editing ? (
                <Button onClick={() => setEditing(true)} size="sm" className="staff-profile-action-button bg-[#0056B3] hover:bg-[#004494]">
                  <Edit2 className="w-4 h-4 mr-2" />
                  Edit Profile
                </Button>
              ) : (
                <div className="staff-profile-edit-actions flex gap-2">
                  <Button onClick={handleSave} size="sm" className="staff-profile-action-button bg-green-600 hover:bg-green-700">
                    <Save className="w-4 h-4 mr-2" />
                    Save
                  </Button>
                  <Button onClick={() => { setEditing(false); setFormData({ name: profileData.profile.name, email: profileData.profile.email, phone: profileData.profile.phone, password: '' }); }} size="sm" variant="outline" className="staff-profile-action-button">
                    Cancel
                  </Button>
                </div>
              )}
            </div>
            <div className="staff-profile-photo-card flex flex-col sm:flex-row sm:items-center gap-4 mb-6 rounded-xl border border-gray-200 bg-gray-50 p-4">
              <div className="staff-profile-photo w-20 h-20 rounded-full bg-[#0056B3] text-white flex items-center justify-center overflow-hidden shrink-0 text-2xl font-bold">
                {profilePhotoUrl ? (
                  <img src={profilePhotoUrl} alt={profileData.profile.name || 'Profile'} className="w-full h-full object-cover" />
                ) : (
                  profileInitial
                )}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-gray-900">Profile Photo</p>
                <div className="text-xs text-gray-500 mt-1 space-y-0.5">
                  <p><span className="font-semibold text-gray-700">Supported formats:</span> JPG, JPEG, PNG, WEBP</p>
                  <p><span className="font-semibold text-gray-700">Maximum file size:</span> 5 MB</p>
                </div>
                <div className="staff-profile-photo-actions flex flex-wrap gap-2 mt-3">
                  <label className={`staff-profile-photo-button inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold cursor-pointer border border-[#0056B3] text-[#0056B3] bg-white hover:bg-blue-50 ${photoUploading ? 'opacity-60 pointer-events-none' : ''}`}>
                    <Camera className="w-4 h-4" />
                    {profilePhotoUrl ? 'Replace Photo' : 'Upload Profile Photo'}
                    <input
                      type="file"
                      accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp"
                      className="hidden"
                      onChange={handlePhotoUpload}
                      disabled={photoUploading || photoRemoving}
                    />
                  </label>
                  {profilePhotoUrl && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handlePhotoRemove}
                      disabled={photoUploading || photoRemoving}
                      className="staff-profile-photo-button text-red-600 border-red-200 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Remove
                    </Button>
                  )}
                </div>
              </div>
            </div>
            {editing ? (
              <div className="staff-profile-details-grid grid grid-cols-1 md:grid-cols-2 gap-4">
                <div><Label>Name</Label><Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} /></div>
                <div><Label>Email</Label><Input value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} /></div>
                <div><Label>Phone</Label><Input value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} /></div>
                <div><Label>New Password (leave blank to keep current)</Label><Input type="password" value={formData.password} onChange={(e) => setFormData({...formData, password: e.target.value})} placeholder="Enter new password" /></div>
                <div><Label>Institution</Label><Input value={profileData.profile.hospital_name} disabled className="bg-gray-100" /></div>
                <div><Label>Department</Label><Input value={profileData.profile.department} disabled className="bg-gray-100" /></div>
                <div><Label>Job Role</Label><Input value={profileData.profile.job_function} disabled className="bg-gray-100" /></div>
              </div>
            ) : (
              <div className="staff-profile-details-grid grid grid-cols-1 md:grid-cols-2 gap-4">
                <div><Label className="text-gray-600">Name</Label><p className="font-medium">{profileData.profile.name}</p></div>
                <div><Label className="text-gray-600">Email</Label><p className="font-medium">{profileData.profile.email}</p></div>
                <div><Label className="text-gray-600">Phone</Label><p className="font-medium">{profileData.profile.phone || 'Not provided'}</p></div>
                <div><Label className="text-gray-600">Institution</Label><p className="font-medium">{profileData.profile.hospital_name}</p></div>
                <div><Label className="text-gray-600">Department</Label><p className="font-medium">{profileData.profile.department}</p></div>
                <div><Label className="text-gray-600">Job Role</Label><p className="font-medium">{profileData.profile.job_function}</p></div>
              </div>
            )}
          </Card>

          {/* Training Metrics */}
          <Card className="staff-profile-card p-6">
            <h2 className="staff-profile-section-title text-[18px] font-bold mb-6" style={{ color: '#0056B3' }}>Training Metrics</h2>
            <div className="staff-profile-metrics-grid grid grid-cols-2 md:grid-cols-5 gap-4">
              <div className="text-center"><p className="text-[22px] font-bold" style={{ color: '#0056B3' }}>{profileData.metrics.total_topics}</p><p className="text-sm text-gray-600">Total</p></div>
              <div className="text-center"><p className="text-[22px] font-bold text-blue-600">{profileData.metrics.visited}</p><p className="text-sm text-gray-600">Visited</p></div>
              <div className="text-center"><p className="text-[22px] font-bold text-green-600">{profileData.metrics.completed}</p><p className="text-sm text-gray-600">Completed</p></div>
              <div className="text-center"><p className="text-[22px] font-bold text-yellow-600">{profileData.metrics.in_progress}</p><p className="text-sm text-gray-600">In Progress</p></div>
              <div className="text-center"><p className="text-[22px] font-bold text-gray-600">{profileData.metrics.untapped}</p><p className="text-sm text-gray-600">Untapped</p></div>
            </div>
          </Card>

          {/* Areas of Strength and Improvement */}
          <div className="staff-profile-two-column grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card className="staff-profile-card p-6">
              <div className="flex items-center space-x-3 mb-4">
                <TrendingUp className="w-6 h-6 text-green-600" />
                <h2 className="text-lg font-bold text-green-600">Areas of Strength</h2>
              </div>
              <p className="text-sm text-gray-600 mb-4">Chapters with ≥{profileData.performance.strength_threshold}% score</p>
              {profileData.performance.strengths.length === 0 ? (
                <p className="text-gray-500 text-sm">No strengths identified yet</p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {profileData.performance.strengths.map(s => (
                    <div 
                      key={s.training_topic_id} 
                      className="px-3 py-1.5 bg-green-50 border border-green-200 rounded-lg hover:bg-green-100 transition-colors"
                    >
                      <span className={s.score === 100 ? "text-sm font-bold text-gray-900" : "text-sm text-gray-900"}>
                        {s.training_topic_name}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </Card>
            <Card className="staff-profile-card p-6">
              <div className="flex items-center space-x-3 mb-4">
                <TrendingDown className="w-6 h-6 text-orange-600" />
                <h2 className="text-lg font-bold text-orange-600">Areas for Improvement</h2>
              </div>
              <p className="text-sm text-gray-600 mb-4">Chapters with ≤{profileData.performance.improvement_threshold}% score</p>
              {profileData.performance.improvements.length === 0 ? (
                <p className="text-gray-500 text-sm">No improvement areas identified</p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {profileData.performance.improvements.map(i => (
                    <div 
                      key={i.training_topic_id} 
                      className="px-3 py-1.5 bg-orange-50 border border-orange-200 rounded-lg hover:bg-orange-100 transition-colors"
                    >
                      <span className="text-sm text-gray-900">{i.training_topic_name}</span>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </div>

          {/* Completed Chapters Table */}
          <Card className="staff-profile-card p-6">
            <h2 className="text-[18px] font-bold mb-6" style={{ color: '#0056B3' }}>My Completed Chapters</h2>
            {loadingTopics ? (
              <div className="flex justify-center py-8">
                <div className="w-8 h-8 border-4 border-[#0056B3] border-t-transparent rounded-full animate-spin"></div>
              </div>
            ) : completedTopics.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No completed chapters yet. Keep learning!</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">Chapter Name</th>
                      <th className="text-center py-3 px-4 font-semibold text-gray-700">Score (%)</th>
                      <th className="text-center py-3 px-4 font-semibold text-gray-700">Completed On</th>
                      <th className="text-center py-3 px-4 font-semibold text-gray-700">Average Feedback Given</th>
                    </tr>
                  </thead>
                  <tbody>
                    {completedTopics.map((topic, index) => (
                      <tr key={topic.topic_id} className={`border-b border-gray-100 hover:bg-gray-50 transition-colors ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                        <td className="py-3 px-4 font-medium text-gray-900">{topic.topic_name}</td>
                        <td className="py-3 px-4 text-center">
                          {topic.score !== null && topic.score !== undefined ? (
                            <span className="inline-block px-3 py-1 bg-green-100 text-green-700 rounded-full font-semibold">
                              {topic.score}%
                            </span>
                          ) : (
                            <span className="text-gray-500">No assessment</span>
                          )}
                        </td>
                        <td className="py-3 px-4 text-center text-gray-600">
                          {topic.completed_at ? new Date(topic.completed_at).toLocaleDateString('en-US', { 
                            year: 'numeric', 
                            month: 'short', 
                            day: 'numeric'
                          }) : '–'}
                        </td>
                        <td className="py-3 px-4 text-center">
                          {topic.average_feedback ? (
                            <div className="flex items-center justify-center space-x-1">
                              {[1, 2, 3, 4, 5].map((star) => (
                                <Star
                                  key={star}
                                  className={`w-4 h-4 ${
                                    star <= Math.round(topic.average_feedback)
                                      ? 'fill-yellow-400 text-yellow-400'
                                      : 'text-gray-300'
                                  }`}
                                />
                              ))}
                              <span className="ml-2 text-sm text-gray-600">({topic.average_feedback})</span>
                            </div>
                          ) : (
                            <span className="text-gray-500">No feedback</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </div>
  );

  if (isMobileProfileSheet) {
    return createPortal(
      <>
        <div className="fixed inset-0 z-50 bg-black/40" onClick={() => onClose(false)} />
        <div className="staff-profile-modal-content staff-mobile-sheet fixed left-0 right-0 bottom-0 z-50 bg-white rounded-t-[24px] shadow-2xl h-[95dvh] max-h-[95dvh] overflow-hidden flex flex-col min-w-0">
          {profileHeader}
          {profileBody}
        </div>
      </>,
      document.body
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="staff-profile-modal-content max-w-6xl max-h-[90vh] p-0 flex flex-col overflow-hidden" hideClose>
        {profileHeader}
        {profileBody}
      </DialogContent>
    </Dialog>
  );
};

export default StaffProfileModal;
